import { DateTime } from "luxon";
import { z } from "zod";

export const CHECKLIST_TEMPLATE_STATUS_VALUES = ["draft", "published", "archived"] as const;

export type ChecklistTemplateStatus = (typeof CHECKLIST_TEMPLATE_STATUS_VALUES)[number];

export const CHECKLIST_TEMPLATE_TRIGGER_TYPE_VALUES = ["app_install", "manual", "recurring"] as const;

export type ChecklistTemplateTriggerType = (typeof CHECKLIST_TEMPLATE_TRIGGER_TYPE_VALUES)[number];

export const RECURRING_PERIOD_VALUES = ["year"] as const;

export type RecurringPeriod = (typeof RECURRING_PERIOD_VALUES)[number];

export type RecurringSchedule = { period: RecurringPeriod; opens_on: string; closes_on: string };

/** `sku` is a MiterSku slug. Manual templates never instantiate on install. */
export type ChecklistTemplateTrigger =
  | { type: "app_install"; sku: string }
  | { type: "manual" }
  | { type: "recurring"; schedule: RecurringSchedule };

const NON_LEAP_YEAR = 2001;

const isMonthDay = (value: string): boolean =>
  DateTime.fromFormat(`${NON_LEAP_YEAR}-${value}`, "yyyy-MM-dd").isValid;

export const MonthDayZodSchema = z.string().refine(isMonthDay, "Expected a calendar date as MM-DD");

export const ChecklistTemplateTriggerZodSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("app_install"), sku: z.string().min(1) }),
  z.object({ type: z.literal("manual") }),
  z.object({
    type: z.literal("recurring"),
    schedule: z
      .object({
        period: z.enum(RECURRING_PERIOD_VALUES),
        opens_on: MonthDayZodSchema,
        closes_on: MonthDayZodSchema,
      })
      .refine(({ opens_on, closes_on }) => opens_on <= closes_on, {
        message: "closes_on can't be before opens_on",
        path: ["closes_on"],
      }),
  }),
]) satisfies z.ZodType<ChecklistTemplateTrigger>;
